/**
 * Integraciones (Ajustes): webhooks que reciben las notas cuando están listas.
 * Sirven para Zapier, Make, n8n o un servidor propio. Desde el plan Personal.
 */
import { useState } from 'react'
import { Link } from 'react-router'
import { PLANS, type Webhook } from '@eco/shared'
import { formatDate } from '../lib/format'
import { useAsync } from '../lib/hooks'
import { errorText, useApp, useToast } from './AppContext'

function estadoTexto(w: Webhook): string {
  if (!w.activo) return 'Desactivado tras muchos fallos: bórralo y vuelve a crearlo'
  if (w.ultimoEstado === null) return 'Sin envíos todavía'
  const ok = w.ultimoEstado >= 200 && w.ultimoEstado < 300
  const cuando = w.ultimoEnvioEn ? ` · ${formatDate(w.ultimoEnvioEn)}` : ''
  if (ok) return `Último envío correcto${cuando}`
  return `Último envío falló (${w.ultimoEstado || 'sin respuesta'})${cuando}`
}

export function IntegrationsSection() {
  const { api, usage } = useApp()
  const toast = useToast()
  const list = useAsync(() => api.webhooks(), [api])
  const [url, setUrl] = useState('')
  const [transcripcion, setTranscripcion] = useState(false)
  const [busy, setBusy] = useState(false)
  const [visible, setVisible] = useState<string | null>(null)
  const limite = usage ? PLANS[usage.plan].webhooks : null

  async function add() {
    setBusy(true)
    try {
      const w = await api.createWebhook({ url: url.trim(), incluirTranscripcion: transcripcion })
      setUrl('')
      setVisible(w.id)
      toast('Integración agregada. Copia el secreto para verificar la firma.')
      await list.reload()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  async function test(w: Webhook) {
    try {
      const r = await api.testWebhook(w.id)
      toast(
        r.ok
          ? `Prueba enviada: respondió ${r.estado}`
          : `El destino no aceptó la prueba (${r.estado || r.error || 'sin respuesta'})`,
        r.ok ? undefined : 'error'
      )
      await list.reload()
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  async function remove(w: Webhook) {
    try {
      await api.deleteWebhook(w.id)
      await list.reload()
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  return (
    <section className="card stack">
      <h2>Integraciones</h2>
      <p className="faint" style={{ fontSize: 12.5, marginTop: -6 }}>
        Cuando las notas de una reunión están listas, eco las envía a estas direcciones (Zapier,
        Make, n8n o tu propio servidor). Cada envío va firmado con la cabecera{' '}
        <code>eco-signature</code>.
      </p>
      {limite === 0 ? (
        <p className="row wrap" style={{ margin: 0 }}>
          <span className="grow muted">
            Las integraciones están disponibles desde el plan Personal.
          </span>
          <Link className="btn" to="/plan">
            Ver planes
          </Link>
        </p>
      ) : (
        <>
          {list.data?.map((w) => (
            <div
              key={w.id}
              className="stack"
              style={{ gap: 4, borderTop: '1px solid var(--line-soft)', paddingTop: 8 }}
            >
              <div className="row wrap">
                <code className="grow" style={{ wordBreak: 'break-all', fontSize: 12.5 }}>
                  {w.url}
                </code>
                <button className="btn sm ghost" onClick={() => void test(w)} disabled={!w.activo}>
                  Enviar prueba
                </button>
                <button className="btn sm ghost danger" onClick={() => void remove(w)}>
                  Borrar
                </button>
              </div>
              <div className="row wrap faint" style={{ fontSize: 12 }}>
                <span className="grow">
                  {estadoTexto(w)}
                  {w.incluirTranscripcion ? ' · incluye la transcripción' : ''}
                </span>
                <button
                  className="btn sm ghost"
                  onClick={() => setVisible((v) => (v === w.id ? null : w.id))}
                  aria-expanded={visible === w.id}
                >
                  {visible === w.id ? 'Ocultar secreto' : 'Ver secreto'}
                </button>
              </div>
              {visible === w.id && (
                <code
                  style={{ fontSize: 12, wordBreak: 'break-all' }}
                  aria-label="Secreto de firma"
                >
                  {w.secreto}
                </code>
              )}
            </div>
          ))}
          {(limite === null || (list.data?.length ?? 0) < limite) && (
            <form
              className="stack"
              style={{ gap: 6 }}
              onSubmit={(e) => {
                e.preventDefault()
                void add()
              }}
            >
              <div className="row">
                <input
                  className="grow"
                  type="url"
                  aria-label="URL del webhook"
                  placeholder="https://hooks.zapier.com/hooks/catch/…"
                  value={url}
                  maxLength={500}
                  onChange={(e) => setUrl(e.target.value)}
                />
                <button className="btn" disabled={busy || !url.trim()}>
                  Agregar
                </button>
              </div>
              <label className="check">
                <input
                  type="checkbox"
                  checked={transcripcion}
                  onChange={(e) => setTranscripcion(e.target.checked)}
                />
                Incluir la transcripción completa (además de las notas)
              </label>
            </form>
          )}
        </>
      )}
    </section>
  )
}
